import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService, Toast } from '../../../core/services/toast.service';

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="fixed bottom-5 right-5 z-50 flex flex-col space-y-3 max-w-sm w-full pointer-events-none px-4 md:px-0">
      @for (toast of toasts(); track toast.id) {
        <div
          [ngClass]="getToastBorder(toast.type)"
          class="pointer-events-auto flex items-start p-4 rounded-xl bg-slate-900/95 text-white shadow-2xl border backdrop-blur-md transition-all duration-300 transform translate-y-0 animate-in fade-in slide-in-from-bottom-5">
          
          <div [ngClass]="getToastIconBg(toast.type)" class="p-2 rounded-lg shrink-0 mr-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="getToastIcon(toast.type)"/>
            </svg>
          </div>

          <div class="flex-1 min-w-0 pr-2">
            <h4 class="text-sm font-semibold tracking-tight text-slate-100">{{ toast.title }}</h4>
            @if (toast.message) {
              <p class="text-xs text-slate-400 mt-0.5 leading-relaxed">{{ toast.message }}</p>
            }
          </div>

          <button
            (click)="dismiss(toast.id)"
            type="button"
            class="text-slate-500 hover:text-white p-1 rounded-lg transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      }
    </div>
  `,
})
export class ToastContainerComponent {
  private readonly toastService = inject(ToastService);
  readonly toasts = this.toastService.toasts;

  dismiss(id: string): void {
    this.toastService.dismiss(id);
  }

  getToastBorder(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return 'border-emerald-500/40';
      case 'warning':
        return 'border-amber-500/40';
      case 'error':
        return 'border-rose-500/40';
      case 'info':
        return 'border-blue-500/40';
    }
  }

  getToastIconBg(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'warning':
        return 'bg-amber-500/20 text-amber-400';
      case 'error':
        return 'bg-rose-500/20 text-rose-400';
      case 'info':
        return 'bg-blue-500/20 text-blue-400';
    }
  }

  getToastIcon(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return 'M5 13l4 4L19 7';
      case 'warning':
        return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';
      case 'error':
        return 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'info':
        return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    }
  }
}
